import { normalizeBookIsbns } from '../../utils/isbn';
import { calculateBookDiscount } from '../../utils/bookPricing';
import { useState } from 'react';
import { FiBookOpen, FiTag, FiX } from 'react-icons/fi';
import { apiCreateBook, apiUpdateBook } from '../../api';
import {
  BasicInfoSection,
  PricingSection,
  StockAndTaxSection,
  PreorderSection,
  PublishingSection,
  DimensionsSection,
  DescriptionSection,
  ImageAndFlagsSection,
} from './BookFormFields';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

// The API stores millimetres; the form displays and accepts centimetres.
const dimensionFields = new Set(['height', 'width', 'spineWidth']);
const toCentimetres = (value) =>
  value == null || value === '' ? '' : String(Number((Number(value) / 10).toFixed(3)));
const toMillimetres = (value) =>
  value === '' ? '' : String(Number((Number(value) * 10).toFixed(2)));

export const BookModal = ({ book, categories, onClose, onSaved }) => {
  const [form, setForm] = useState({
    sku: book?.sku || '',
    title: book?.title || '',
    subtitle: book?.subtitle || '',
    author: book?.author || '',
    price: book?.price ?? '',
    originalPrice: book?.originalPrice ?? '',
    discount: calculateBookDiscount(book?.price, book?.originalPrice),
    usdPrice: book?.usdPrice ?? '',
    usdOriginalPrice: book?.usdOriginalPrice ?? '',
    usdDiscount: calculateBookDiscount(book?.usdPrice, book?.usdOriginalPrice),
    taxInclusive: book?.taxInclusive !== undefined ? book.taxInclusive : true,
    taxRate: book?.taxRate ?? '',
    taxLabel: book?.taxLabel || '',
    categoryId: book?.categoryId || '',
    publisher: book?.publisher || '',
    description: book?.description || '',
    authorBio: book?.authorBio || '',
    pages: book?.pages ?? '',
    language: book?.language || 'English',
    stock: book?.stock ?? '',
    returnWindowDays: book?.returnWindowDays ?? '',
    internationalReturnWindowDays: book?.internationalReturnWindowDays ?? '',
    bestsellerRank: book?.bestsellerRank ?? '',
    isBestseller: book?.isBestseller || false,
    isPreorder: book?.isPreorder || false,
    preorderMessage: book?.preorderMessage || '',
    isbn10: book?.isbn10 || '',
    isbn13: book?.isbn13 || '',
    binding: book?.binding || 'Paperback',
    edition: book?.edition || '',
    readingAge: book?.readingAge || '',
    countryOfOrigin: book?.countryOfOrigin || 'India',
    netQuantity: book?.netQuantity || '1 Count',
    preorderReleaseDate: book?.preorderReleaseDate ? book.preorderReleaseDate.substring(0, 10) : '',
    publisherDate: book?.publisherDate ? book.publisherDate.substring(0, 10) : '',
    weight: book?.weight || '',
    height: toCentimetres(book?.height),
    width: toCentimetres(book?.width),
    spineWidth: toCentimetres(book?.spineWidth),
    returnable: book?.returnable !== undefined ? book.returnable : true,
    relatedCategories: Array.isArray(book?.relatedCategories)
      ? book.relatedCategories.join(', ')
      : book?.relatedCategories || '',
  });
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [dirty, setDirty] = useState(false);
  const canClose = useUnsavedChanges(dirty && !saving);
  const updateForm = (nextForm) => {
    setDirty(true);
    setForm(nextForm);
  };
  const requestClose = async () => {
    if (await canClose()) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');
    let identity;
    try {
      if (form.sku.trim().length > 100) throw new Error('SKU cannot exceed 100 characters');
      identity = normalizeBookIsbns({ isbn10: form.isbn10, isbn13: form.isbn13 });
      if (form.taxRate !== '' && (!/^\d+(\.\d{1,2})?$/.test(String(form.taxRate)) || Number(form.taxRate) > 100)) {
        throw new Error('Tax rate must be between 0 and 100 with at most two decimal places');
      }
    } catch (error) {
      setValidationError(error.message);
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries({ ...form, isbn10: identity.isbn10 || '', isbn13: identity.isbn13 || '' }).forEach(([k, v]) => {
        if (k === 'preorderReleaseDate' && !form.isPreorder) return;
        if (k === 'relatedCategories') {
          const arr = String(v)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          formData.append(k, JSON.stringify(arr));
        } else if (dimensionFields.has(k)) {
          formData.append(k, toMillimetres(v));
        } else {
          formData.append(k, ['sku', 'preorderMessage'].includes(k) ? String(v || '').trim() : v);
        }
      });
      if (image) formData.append('image', image);

      if (book) {
        await apiUpdateBook(book.id, formData);
      } else {
        await apiCreateBook(formData);
      }
      onSaved();
    } catch (error) { setValidationError(error.message || 'Could not save the product'); } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="book-modal-title" className="flex w-full max-w-5xl max-h-[90vh] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-3">
          <h2 id="book-modal-title" className="flex items-center gap-2 text-xl font-bold text-slate-800"><FiBookOpen className="text-cyan-800" aria-hidden="true" />{book ? 'Edit Book' : 'Add New Book'}</h2>
          <div className="flex items-center gap-3">
            <button type="button" aria-label="Close book form" onClick={requestClose} className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800">
              <FiX size={20} />
            </button>
          </div>
        </div>
        <form
          onSubmit={handleSubmit}
          onWheelCapture={(event) => {
            // Remove focus before the browser's wheel action can step a number.
            // Leave the event uncancelled so the modal still scrolls normally.
            if (event.target instanceof HTMLInputElement && event.target.type === 'number') {
              event.target.blur();
            }
          }}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 pb-4">
            {validationError && <p role="alert" className="text-sm text-red-600">{validationError}</p>}
            <BasicInfoSection form={form} setForm={updateForm} categories={categories} />
            <PricingSection form={form} setForm={updateForm} />
            <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
              <div className="min-w-0 space-y-3">
                <PreorderSection form={form} setForm={updateForm} />
                <DescriptionSection form={form} setForm={updateForm} />
              </div>
              <div className="min-w-0 space-y-3">
                <PublishingSection form={form} setForm={updateForm} />
              </div>
            </div>
            <StockAndTaxSection form={form} setForm={updateForm} />
            <DimensionsSection form={form} setForm={updateForm} />
            <ImageAndFlagsSection
              currentImage={book?.image}
              form={form}
              setForm={updateForm}
              onImageChange={(file) => { setImage(file); setDirty(true); }}
            />
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-blue-100 bg-blue-50/60 px-4 py-3">
            <p className="mr-auto flex max-w-md items-start gap-1.5 text-[10px] text-slate-500"><FiTag className="mt-0.5 shrink-0" aria-hidden="true" />{form.taxInclusive ? 'Tax is included in the selling price and will not be added again.' : 'Tax is added at checkout after coupon discounts.'} Leave the rate blank for no tax.</p>
            <button
              type="button"
              onClick={requestClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : book ? 'Update Book' : 'Create Book'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
